<template>
  <div>
    <template slot="header">
      <h3 v-if="liquidated" class="mb-0">Liquidaciones a Facturar</h3>
      <h3 v-else class="mb-0">Liquidaciones</h3>
    </template>

    <div>
      <div
        v-if="orderItems.length && $can('see_settlement_button', 'payment_access')"
        class="col-12 mb-3 d-flex flex-column align-items-end"
      >
        <base-button
          v-if="!liquidated"
          cy-id="generate-liquidation"
          type="primary"
          class="h-50 mr-3"
          :disabled="!canLiquidate"
          @click="generateLiquidation"
        >
          Generar Liquidación
        </base-button>

        <small v-if="!canLiquidate" class="text-muted mt-1" style="max-width: 220px">
          Para poder generar una liquidación debe filtrar por un
          {{ isBySeller ? 'asesor' : 'distribuidor' }}
        </small>
      </div>

      <div class="col-12 d-flex justify-content-sm-between align-items-end mt-3 mb-5">
        <el-select v-model="pagination.perPage" class="" placeholder="Per page">
          <el-option
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
            class="select-primary"
          />
        </el-select>

        <slot name="filters"></slot>
      </div>
    </div>

    <div cy-id="table" class="table-responsive">
      <el-table :data="orderItems" header-row-class-name="thead-light">
        <el-table-column label="Fecha Liq." min-width="135px" prop="date">
          <template #default="{ row }">
            <span cy-id="date-table-value">{{ row.order.liquidationDate | date }}</span>
          </template>
        </el-table-column>
        <el-table-column label="N° Pedido" min-width="144px" prop="quote">
          <template #default="{ row }">
            <span>{{ row.order.code }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Campaña" min-width="130px" prop="campaign">
          <template #default="{ row }">
            <span cy-id="campaign-table-value">{{ row.article.campaign.name }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Razón Social" min-width="155px" prop="producer">
          <template #default="{ row }">
            <span>{{ row.order.producer.businessName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Distribuidor" min-width="155px" prop="distributor">
          <template #default="{ row }">
            <span cy-id="distributor-table-value">{{ row.order.producer.distributor.name }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Asesor Técnico" min-width="170px" prop="seller">
          <template #default="{ row }">
            <span cy-id="seller-table-value">{{ row.order.producer.distributor.seller.name }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Artículo" min-width="130px" prop="article">
          <template #default="{ row }">
            <span>{{ row.article.description }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Cantidad" min-width="130px" prop="quantity">
          <template #default="{ row }">
            <span>{{ row.quantity }}</span>
          </template>
        </el-table-column>
        <el-table-column label="P. Unitario" min-width="145px" prop="price">
          <template #default="{ row }">
            <span>{{ row.price }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Costo Distr." min-width="150px" prop="cost">
          <template #default="{ row }">
            <span>{{ row.distributorCost }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="isBySeller" label="Com. %" min-width="120px" prop="commission">
          <template #default="{ row }">
            <span>{{ (row.order.producer.distributor.seller.commission / 100) | percentage }}</span>
          </template>
        </el-table-column>
        <el-table-column v-else label="Com. x Bolsa" min-width="120px" prop="commission">
          <template #default="{ row }">
            <span>{{ (row.price - row.distributorCost) | decimals }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="isBySeller" label="Total Comisión" min-width="170px" prop="total-commission">
          <template #default="{ row }">
            <span cy-id="commission-value-usd">{{ getSellerCommissionAmount(row) | toCurrencyUSD }}</span>
          </template>
        </el-table-column>
        <el-table-column v-else label="Total Comisión" min-width="170px" prop="total-commission">
          <template #default="{ row }">
            <span cy-id="commission-value-usd">{{ getDistributorCommissionAmount(row) | toCurrencyUSD }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Cot. Dólar" min-width="140px" prop="quote">
          <template #default="{ row }">
            <span>{{ getDollarQuotation(row) | toCurrencyARS }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="isBySeller" label="Total Com. Pesos" min-width="150px" prop="total-commission-pesos">
          <template #default="{ row }">
            <span cy-id="commission-value-ars">
              {{ (getSellerCommissionAmount(row) * getDollarQuotation(row)) | toCurrencyARS }}
            </span>
          </template>
        </el-table-column>
        <el-table-column v-else label="Total Com. Pesos" min-width="150px" prop="total-commission-pesos">
          <template #default="{ row }">
            <span cy-id="commission-value-ars">
              {{ (getDistributorCommissionAmount(row) * getDollarQuotation(row)) | toCurrencyARS }}
            </span>
          </template>
        </el-table-column>
        <el-table-column v-if="liquidated" label="Liquidación" min-width="120px" prop="liquidation">
          <template #default="{ row }">
            <a :href="getLiquidationByRol(row).liquidationPath" target="_blank">{{ getLiquidationByRol(row).id }}</a>
          </template>
        </el-table-column>
        <el-table-column v-if="liquidated" label="Factura" min-width="120px" prop="invoice">
          <template #default="{ row }">
            <a
              v-if="getLiquidationByRol(row).invoiceNumber && getLiquidationByRol(row).invoicePath"
              :href="getLiquidationByRol(row).invoicePath"
              cy-id="invoice-file"
              target="_blank"
            >
              {{ getLiquidationByRol(row).invoiceNumber }}
            </a>
          </template>
        </el-table-column>

        <el-table-column v-if="liquidated" label="cambio" min-width="120px" prop="invoice">
          <template #default="{ row }">
            <button title="Eliminar" class="btn base-button btn-outline-danger btn-sm" @click="deleteLiquidation(row)">
              <i class="fas fa-trash" />
            </button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap mt-5">
      <div class="">
        <p class="card-category">
          Mostrando {{ from + 1 }} a {{ to }} de un total de {{ pagination.total }} liquidaciones
        </p>
      </div>
      <base-pagination
        v-model="pagination.currentPage"
        :per-page="pagination.perPage"
        :show-first-page-button="true"
        :show-last-page-button="true"
        :total="pagination.total"
        class="pagination-no-border"
        @input="changePage"
      >
      </base-pagination>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option } from 'element-ui';
import { BasePagination } from '@/components';
import PaginationOnServerMixin from '@/mixins/paginationOnServerMixin';
import swal from 'sweetalert2';
import { round } from '@/util/math';

export default {
  name: 'LiquidationTable',
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    BasePagination,
  },

  mixins: [PaginationOnServerMixin],

  props: {
    isBySeller: {
      type: Boolean,
      required: true,
    },

    distributorSelected: {
      type: Number,
      default: null,
    },

    sellerSelected: {
      type: Number,
      default: null,
    },

    campaignSelected: {
      type: Number,
      default: null,
    },

    startDate: {
      type: String,
      default: null,
    },

    endDate: {
      type: String,
      default: null,
    },

    liquidated: {
      type: Boolean,
      required: true,
    },

    searchQuery: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      orderItems: [],
      amount: 0,
      modelName: 'ordersItems',
    };
  },

  computed: {
    canLiquidate() {
      return this.isBySeller ? this.sellerSelected !== null : this.distributorSelected !== null;
    },
  },

  watch: {
    distributorSelected() {
      this.pagination.currentPage = 1;
      this.fetchTableData();
    },

    sellerSelected() {
      this.pagination.currentPage = 1;
      this.fetchTableData();
    },

    campaignSelected() {
      this.pagination.currentPage = 1;
      this.fetchTableData();
    },

    startDate() {
      this.pagination.currentPage = 1;
      this.fetchTableData();
    },

    searchQuery() {
      this.pagination.currentPage = 1;
      this.fetchTableData();
    },
  },

  mounted() {
    this.fetchTableData();
  },

  methods: {
    getDollarQuotation(item) {
      return Number(item.order.canceledInPesos / item.order.total);
    },

    getDistributorCommission(item) {
      return (item.price - item.distributorCost) / item.price;
    },

    getSellerCommission(item) {
      return ((item.distributorCost / item.price) * Number(item.order.producer.distributor.seller.commission)) / 100;
    },

    getDistributorCommissionAmount(item) {
      return Number(this.getDistributorCommission(item) * item.quantity * item.price);
    },

    getSellerCommissionAmount(item) {
      return this.getSellerCommission(item) * item.quantity * item.price;
    },

    getLiquidationByRol(row) {
      return this.isBySeller
        ? row.order.liquidations.find((liquidation) => liquidation.liquidationableType == 'sellers')
        : row.order.liquidations.find((liquidation) => liquidation.liquidationableType == 'distributors');
    },

    generateLiquidation() {
      const liquidated = this.isBySeller ? 'filter[seller_liquidated]' : 'filter[distributor_liquidated]';

      const params = {
        'filter[seller]': this.sellerSelected,
        'filter[distributor]': this.distributorSelected,
        'filter[campaign]': this.campaignSelected,
        'filter[liquidated]': 1,
        'filter[rangeDate]': this.startDate && this.endDate ? `${this.startDate},${this.endDate}` : null,
        [liquidated]: true,
      };

      const payload = {
        amount: this.amount,
        orderIds: this.orderIds,
        liquidationableId: this.isBySeller ? this.sellerId : this.distributorId,
        liquidationableType: this.isBySeller ? 'sellers' : 'distributors',
      };

      this.$services
        .resource('liquidations')
        .create(payload, params)
        .then(() => {
          swal({
            title: 'Listo!',
            text: 'La liquidación fue creada exitosamente.',
            buttonsStyling: false,
            confirmButtonClass: 'btn btn-success',
            type: 'success',
          }).then(() => {
            this.$router.go(0);
          });
        });
    },

    fetchTableData() {
      this.pagination.perPage = this.pagination.perPage || this.perPage;
      this.pagination.currentPage = this.pagination.currentPage || this.page;
      const liquidated = this.isBySeller ? 'filter[seller_liquidated]' : 'filter[distributor_liquidated]';
      const params = {
        'filter[seller]': this.sellerSelected,
        'filter[distributor]': this.distributorSelected,
        'filter[campaign]': this.campaignSelected,
        'filter[liquidated]': 1,
        'filter[search]': this.searchQuery,
        'filter[rangeDate]': this.startDate && this.endDate ? `${this.startDate},${this.endDate}` : null,
        [liquidated]: this.liquidated,
        include: ['order.producer.distributor.seller', 'article.campaign', 'order.liquidations'],
        append: ['total', 'total-in-pesos', 'canceled'],
        perPage: this.pagination.perPage,
        page: this.pagination.currentPage,
      };

      this.$services
        .resource('order_items')
        .get(params)
        .then((res) => {
          this.setMeta(res.data.meta);
          return res.data.data;
        })
        .then((data) => {
          const ids = data.items.map((item) => item.id);

          this.orderItems = data.items;
          this.orderIds = [...new Set(ids)];
          this.amount = round(this.getAmount());
          this.setIds();
        });
    },

    setIds() {
      if (this.orderItems.length > 0) {
        if (this.isBySeller) {
          this.sellerId = this.getSellerId();
        } else {
          this.distributorId = this.getDistributorId();
        }
      }
    },

    getDistributorId() {
      if (this.orderItems.length > 0) {
        return this.orderItems.at(0).order.producer.distributorId;
      }

      return null;
    },

    getSellerId() {
      if (this.orderItems.length > 0) {
        return this.orderItems.at(0).order.producer.distributor.sellerId;
      }

      return null;
    },

    getAmount() {
      return this.isBySeller
        ? this.orderItems.reduce(
            (sum, item) => sum + this.getSellerCommissionAmount(item) * this.getDollarQuotation(item),
            0
          )
        : this.orderItems.reduce(
            (sum, item) => sum + this.getDistributorCommissionAmount(item) * this.getDollarQuotation(item),
            0
          );
    },

    deleteLiquidation(row) {
      swal({
        title: 'Se eliminará el pedido',
        text: '¿Desea proceder?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        buttonsStyling: false,
        confirmButtonClass: 'btn btn-danger',
        cancelButtonClass: 'btn btn-secondary',
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (result.value) {
          this.$services
            .resource('liquidations')
            .delete(row.order.liquidations[0].id)
            .then(() => swal('Listo!', 'La liquidación ha sido eliminada con éxito', 'success'))
            .then(() => {
              this.fetchTableData();
            });
        }
      });
    },
  },
};
</script>

<style scoped>
button:disabled:hover {
  transform: none;
  box-shadow: none;
}
</style>
